import { useMemo } from "react";
import type { Candidate } from "@/components/candidates/types";

export const useSourceAnalytics = (candidates: Candidate[]) => {
  const candidatesBySource = useMemo(() => {
    const sources: Record<string, number> = {};
    
    candidates.forEach(candidate => {
      const source = candidate.source || 'Unknown';
      
      if (!sources[source]) {
        sources[source] = 0;
      }
      
      sources[source]++;
    });
    
    return Object.entries(sources)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [candidates]);
  
  const candidatesByStatus = useMemo(() => {
    const statusMap: Record<string, Record<string, number>> = {};
    const sources = Array.from(new Set(candidates.map(c => c.source || 'Unknown')));
    const statuses = ['new', 'contacted', 'interview', 'offer', 'hired', 'rejected'];
    
    sources.forEach(source => {
      statusMap[source] = {};
      statuses.forEach(status => {
        statusMap[source][status] = 0;
      });
    });
    
    candidates.forEach(candidate => {
      const source = candidate.source || 'Unknown';
      const status = candidate.status || 'new';
      
      if (statusMap[source] && statusMap[source][status] !== undefined) {
        statusMap[source][status]++;
      }
    });
    
    const result = sources.map(source => {
      const data: Record<string, any> = { name: source };
      let total = 0;
      
      statuses.forEach(status => {
        const count = statusMap[source][status] || 0;
        data[status] = count;
        total += count;
      });
      
      data.total = total;
      return data;
    });
    
    return result.sort((a, b) => b.total - a.total).slice(0, 6);
  }, [candidates]);
  
  const conversionRateBySource = useMemo(() => {
    const conversionMap: Record<string, { total: number, hired: number }> = {};
    
    candidates.forEach(candidate => {
      const source = candidate.source || 'Unknown';
      
      if (!conversionMap[source]) {
        conversionMap[source] = { total: 0, hired: 0 };
      }
      
      conversionMap[source].total++;
      
      if (candidate.status === 'hired') {
        conversionMap[source].hired++;
      }
    });
    
    return Object.entries(conversionMap)
      .map(([source, { total, hired }]) => ({
        name: source,
        rate: total > 0 ? (hired / total) * 100 : 0,
        total,
        hired
      }))
      .filter(item => item.total >= 5)
      .sort((a, b) => b.rate - a.rate)
      .slice(0, 5);
  }, [candidates]);

  return {
    candidatesBySource,
    candidatesByStatus,
    conversionRateBySource
  };
};
